import React, { useState, useEffect, useContext } from "react"
import { SessionContext } from "context/sessionContext"
import { Link, useNavigate } from "react-router-dom"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
} from "reactstrap"
import { categoryApi } from "apis/CategoryApi"
import Swal from "sweetalert2"

const ViewCategories = () => {
  document.title = "Assessment Categories"
  const navigate = useNavigate()
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [expandedId, setExpandedId] = useState(null)

  // ── Pagination ────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  const { Session } = useContext(SessionContext)
  const activeSessionId = Session || sessionStorage.getItem("SessionId")

  useEffect(() => {
    if (activeSessionId) {
      fetchCategories(1)
    }
  }, [activeSessionId])

  const fetchCategories = async (page = 1) => {
    setLoading(true)
    try {
      const res = await categoryApi.getAllCategory(page, activeSessionId ? parseInt(activeSessionId, 10) : null)
      const data = res?.data ?? res
      if (data?.status === 200 && Array.isArray(data?.categories)) {
        setCategories(data.categories)
        setTotalPages(data.total_pages ?? 1)
        setTotalCount(data.total_count ?? data.categories.length)
        setCurrentPage(data.page_number ?? page)
      } else {
        setCategories([])
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error)
      Swal.fire({
        title: "Error",
        text: "Failed to fetch categories.",
        icon: "error",
        confirmButtonColor: "#556ee6",
      })
      setCategories([])
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange = page => {
    if (page < 1 || page > totalPages) return
    setExpandedId(null)
    fetchCategories(page)
  }

  const toggleExpand = id => {
    setExpandedId(prev => (prev === id ? null : id))
  }

  const formatDate = dateStr => {
    if (!dateStr) return "—"
    const d = new Date(dateStr)
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
  }

  // ── Pagination ────────────────────────────────────────────────────────
  const renderPagination = () => {
    if (totalPages <= 1) return null
    const pages = []
    const maxVisible = 5
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1)
    for (let i = start; i <= end; i++) pages.push(i)

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Showing page <strong>{currentPage}</strong> of{" "}
          <strong>{totalPages}</strong>&nbsp;|&nbsp;Total:{" "}
          <strong>{totalCount}</strong> categories
        </small>
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(1)}>
              «
            </button>
          </li>
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage - 1)}
            >
              ‹
            </button>
          </li>
          {pages.map(p => (
            <li
              key={p}
              className={`page-item ${p === currentPage ? "active" : ""}`}
            >
              <button className="page-link" onClick={() => handlePageChange(p)}>
                {p}
              </button>
            </li>
          ))}
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage + 1)}
            >
              ›
            </button>
          </li>
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(totalPages)}
            >
              »
            </button>
          </li>
        </ul>
      </div>
    )
  }

  // ── EDIT ─────────────────────────────────────────────────────────────────
  const handleEdit = cat => {
    navigate("/category-update", { state: { category: cat } })
  }

  // ── DELETE ────────────────────────────────────────────────────────────────
  const handleDelete = async cat => {
    const result = await Swal.fire({
      title: "Delete Category?",
      text: `Are you sure you want to delete "${cat.category_name}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#556ee6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    })

    if (!result.isConfirmed) return

    try {
      const res = await categoryApi.deleteCategory({ category_id: cat.id })
      const data = res?.data ?? res
      if (data?.status === 200) {
        Swal.fire({
          title: "Deleted!",
          text: `"${cat.category_name}" has been deleted.`,
          icon: "success",
          confirmButtonColor: "#556ee6",
          timer: 2000,
          showConfirmButton: false,
        })
        const newPage =
          categories.length === 1 && currentPage > 1
            ? currentPage - 1
            : currentPage
        fetchCategories(newPage)
      } else {
        throw new Error(data?.msg || "Delete failed")
      }
    } catch (error) {
      console.error("Failed to delete category:", error)
      Swal.fire({
        title: "Error",
        text: error?.message || "Failed to delete category.",
        icon: "error",
        confirmButtonColor: "#556ee6",
      })
    }
  }

  const isTopicWise = cat => cat.marking === "Subject + Topic Wise"
  const isCoCategory = cat => cat.subjects && cat.subjects.some(
    s => s.subject_code === "" || String(s.subject_code).startsWith("CO")
  )

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Container fluid>
            <Breadcrumb
              title="Examination"
              breadcrumbItem="All Created Categories"
            />

            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody className="border-bottom">
                    <div className="d-flex align-items-center">
                      <h4 className="mb-0 card-title flex-grow-1">
                        View Categories
                      </h4>
                      <div className="flex-shrink-0">
                        <Link to="/category-master">
                          <Button
                            color="primary"
                            className="waves-effect waves-light"
                          >
                            + Create Category
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardBody>

                  <CardBody>
                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" />
                      </div>
                    ) : categories.length === 0 ? (
                      <div className="text-center py-5 text-muted">
                        <i
                          className="bx bx-folder-open"
                          style={{ fontSize: "40px" }}
                        />
                        <p className="mt-2">No categories found.</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-bordered align-middle">
                          <thead className="table-light">
                            <tr>
                              <th width="60">Sr.No</th>
                              <th>Category Name</th>
                              <th>Evaluation Type</th>
                              <th>Marking</th>
                              <th>Classes</th>
                              <th>Subjects</th>
                              <th>Created At</th>
                              <th width="80">Details</th>
                              <th width="130">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {categories.map((cat, index) => (
                              <React.Fragment key={cat.id}>
                                <tr>
                                  <td>{(currentPage - 1) * 10 + index + 1}</td>
                                  <td>
                                    <strong>{cat.category_name}</strong>
                                  </td>
                                  <td>{cat.evaluation_type}</td>
                                  <td>{cat.marking}</td>
                                  <td>
                                    {Array.isArray(cat.st_class) &&
                                    cat.st_class.length > 0
                                      ? cat.st_class.map((cls, i) => (
                                          <Badge
                                            key={i}
                                            color="dark"
                                            className="me-1"
                                            style={{ fontSize: "12px" }}
                                          >
                                            {cls.name?.toUpperCase() || "—"}
                                          </Badge>
                                        ))
                                      : "—"}
                                  </td>
                                  <td>
                                    {cat.subjects && cat.subjects.length > 0
                                      ? `${cat.subjects.length} subject${
                                          cat.subjects.length > 1 ? "s" : ""
                                        }`
                                      : "—"}
                                  </td>
                                  <td>{formatDate(cat.created_at)}</td>
                                  <td>
                                    <Button
                                      color="link"
                                      size="sm"
                                      className="p-0"
                                      title="See Subject Details"
                                      onClick={() => toggleExpand(cat.id)}
                                    >
                                      <i
                                        className={`mdi mdi-chevron-${
                                          expandedId === cat.id ? "up" : "down"
                                        } me-1`}
                                        style={{ fontSize: "16px" }}
                                      />
                                      Details
                                    </Button>
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <Button
                                        color="warning"
                                        size="sm"
                                        title="Edit Category"
                                        onClick={() => handleEdit(cat)}
                                      >
                                        <i className="mdi mdi-pencil" />
                                      </Button>
                                      <Button
                                        color="danger"
                                        size="sm"
                                        title="Delete Category"
                                        onClick={() => handleDelete(cat)}
                                      >
                                        <i className="mdi mdi-delete" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>

                                {/* Expanded detail row */}
                                {expandedId === cat.id && (
                                  <tr>
                                    <td
                                      colSpan={9}
                                      style={{
                                        background: "#f8f9fc",
                                        padding: "16px 24px",
                                      }}
                                    >
                                      <Row>
                                        <Col md={12}>
                                          <h6
                                            className="mb-3"
                                            style={{
                                              fontSize: "13px",
                                              fontWeight: 600,
                                              color: "#495057",
                                            }}
                                          >
                                            Subjects & Topics
                                          </h6>
                                          {cat.subjects &&
                                          cat.subjects.length > 0 ? (
                                            <div className="table-responsive">
                                              <table className="table table-sm table-bordered mb-0">
                                                <thead className="table-light">
                                                  <tr>
                                                    <th width="40">#</th>
                                                    <th>Subject Name</th>
                                                    {!isCoCategory(cat) && <th>Subject Code</th>}
                                                    {/* Sequence column only for Subject Wise marking */}
                                                    {!isTopicWise(cat) && (
                                                      <th>Sequence</th>
                                                    )}
                                                    {/* Topics column only for Subject + Topic Wise marking */}
                                                    {isTopicWise(cat) && (
                                                      <th>Topics</th>
                                                    )}
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {cat.subjects.map(
                                                    (sub, sIdx) => (
                                                      <tr key={sIdx}>
                                                        <td>{sIdx + 1}</td>
                                                        <td>
                                                          {sub.subject_name}
                                                        </td>
                                                        {!isCoCategory(cat) && (
                                                           <td>
                                                             {sub.subject_code}
                                                           </td>
                                                         )}
                                                        {/* Subject Wise: show sequence_no */}
                                                        {!isTopicWise(cat) && (
                                                          <td>
                                                            {sub.sequence_no ||
                                                              "—"}
                                                          </td>
                                                        )}
                                                        {/* Subject + Topic Wise: show topics list */}
                                                        {isTopicWise(cat) && (
                                                          <td>
                                                            {sub.topics &&
                                                            sub.topics.length >
                                                              0 ? (
                                                              <ul className="list-unstyled mb-0">
                                                                {sub.topics.map(
                                                                  (
                                                                    topic,
                                                                    tIdx
                                                                  ) => (
                                                                    <li
                                                                      key={tIdx}
                                                                      className="d-flex align-items-center mb-1"
                                                                    >
                                                                      <Badge
                                                                        color="light"
                                                                        className="me-2"
                                                                      >
                                                                        {
                                                                          <strong>
                                                                            {
                                                                              topic.sequence_no
                                                                            }
                                                                          </strong>
                                                                        }
                                                                      </Badge>
                                                                      {
                                                                        topic.topic_name
                                                                      }
                                                                    </li>
                                                                  )
                                                                )}
                                                              </ul>
                                                            ) : (
                                                              <span className="text-muted">
                                                                No topics
                                                              </span>
                                                            )}
                                                          </td>
                                                        )}
                                                      </tr>
                                                    )
                                                  )}
                                                </tbody>
                                              </table>
                                            </div>
                                          ) : (
                                            <span className="text-muted">
                                              No subjects configured.
                                            </span>
                                          )}
                                        </Col>
                                      </Row>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {renderPagination()}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </React.Fragment>
  )
}

export default ViewCategories
